import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { clearAllLocal } from "@/lib/consent";

export const DeleteAccount = ({ u }) => {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const local = !!u.has_password;
  const ready = local ? value.length > 0 : value.trim().toUpperCase() === "DELETE";
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.delete("/account", { data: local ? { password: value } : { confirm: value } });
      clearAllLocal(); await logout();
      toast.success(data.email_sent ? "Your account has been deleted. A confirmation email is on its way." : "Your account and practice data have been deleted.");
      nav("/", { replace: true });
    } catch (x) { toast.error(errMsg(x)); setBusy(false); }
  };
  if (u.is_admin) return null;
  return (
    <div data-testid="account-delete-section" className="mt-6 rounded-2xl border border-rose-500/30 bg-rose-950/20 p-6">
      <h2 className="font-display text-lg font-bold text-white">Delete my account</h2>
      <p className="mt-1 max-w-2xl text-sm text-slate-400">Permanently removes your profile, test history, practice progress, voice-response transcripts, tutor chats and feedback. Payment and Terms-acceptance records are kept as required for tax and disputes. This cannot be undone and does not entitle you to a refund.</p>
      <button data-testid="account-delete-open-btn" onClick={() => { setValue(""); setOpen(true); }} className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-500/60 px-5 py-2.5 text-sm font-bold text-rose-300 transition-colors hover:bg-rose-500 hover:text-white"><Trash2 className="h-4 w-4" />Delete account and recordings</button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="account-delete-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete your account permanently?</DialogTitle>
            <DialogDescription>{local ? "Enter your password to confirm." : "Type DELETE to confirm."} Any remaining pass days will be lost.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="account-delete-input" type={local ? "password" : "text"} value={value} onChange={(e) => setValue(e.target.value)} autoFocus maxLength={128}
              placeholder={local ? "Your password" : "DELETE"} autoComplete={local ? "current-password" : "off"}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/30" />
            <div className="flex justify-end gap-2">
              <button type="button" data-testid="account-delete-cancel-btn" onClick={() => setOpen(false)} disabled={busy} className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button data-testid="account-delete-confirm-btn" disabled={!ready || busy} className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-rose-700 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Delete forever</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
